import { createSlice } from "@reduxjs/toolkit";
import { generateSessionId, setSession, clearSession } from "../common/utils/sessionManager";

const ROLE_KEYS = ["admin", "student"];

const getStorageKey = (role) => `${role}_loginState`;

const loadRoleState = (role) => {
  try {
    const raw = localStorage.getItem(getStorageKey(role));
    if (!raw) return { user: null, isLoggedIn: false };
    const parsed = JSON.parse(raw);
    return {
      user: parsed?.user ?? null,
      isLoggedIn: !!parsed?.isLoggedIn,
    };
  } catch {
    return { user: null, isLoggedIn: false };
  }
};

const loadStateFromStorage = () => {
  try {
    const old = localStorage.getItem("loginState");
    if (old && !localStorage.getItem(getStorageKey("admin")) && !localStorage.getItem(getStorageKey("student"))) {
      const parsed = JSON.parse(old);
      const role = parsed?.user?.role === "student" ? "student" : "admin";
      localStorage.setItem(getStorageKey(role), old);
      localStorage.removeItem("loginState");
    }
  } catch (_) {}
  return {
    admin: loadRoleState("admin"),
    student: loadRoleState("student"),
    loading: false,
    error: null,
  };
};

const initialState = loadStateFromStorage();

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      const { role, ...rest } = action.payload;
      if (!ROLE_KEYS.includes(role)) return;
      const sessionId = generateSessionId();
      const user = { ...rest, role, sessionId };
      state[role] = { user, isLoggedIn: true };
      try {
        localStorage.setItem(getStorageKey(role), JSON.stringify({ user, isLoggedIn: true }));
        setSession(user.email, sessionId, role);
        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("session_channel");
          channel.postMessage({ type: "NEW_SESSION", email: user.email, role, sessionId });
          channel.close();
        }
      } catch (err) {
        console.error("Error saving login state to localStorage:", err);
      }
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state, action) => {
      const role = action.payload;
      if (!ROLE_KEYS.includes(role)) return;
      state[role] = { user: null, isLoggedIn: false };
      state.error = null;
      try {
        localStorage.removeItem(getStorageKey(role));
        clearSession(role);
      } catch (err) {
        console.error("Error clearing login state from localStorage:", err);
      }
    },
  },
});

const { actions, reducer: loginReducer } = loginSlice;
export const { loginStart, loginSuccess, loginFailure, logout } = actions;

export default loginReducer;
